import { Event } from '@livecontrol/scheduler/model';
import { Scheduler } from '@livecontrol/scheduler/store';
import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { FlattenSimpleInterpolation } from 'styled-components';
import { Alert, Select, Tag, Toggle, Tooltip, useAlert, useConfirm } from '../../components';
import type { SelectOption } from '../../components';
import type { ScheduledEvent } from '../event-details';
import { useDefcon, useStream, useUpdateEventAdminFields } from '../event-details';

interface Props {
  scheduledEvent: ScheduledEvent;
}

type EventDestination = NonNullable<ScheduledEvent['destinations']>[number];

/*
 * A destination the event is streaming to whose authorisation has lapsed: LiveControl is pushing,
 * but nothing is coming out the other end. Named rather than inlined because the status string is
 * what the badge colour keys off.
 */
const NOT_DELIVERING = 'Not delivering';

/* The entry graphql_api appends to every event, so it is recognised by name rather than type. */
const LIVECONTROL_PLAYER = 'LiveControl Player';

interface DetailRow {
  label: string;
  value: string;
  /** Rendered as an anchor when true. */
  link?: boolean;
}

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 13px;
  min-height: 34px;
  font-size: 15px;
  font-weight: 700;
  color: var(--white);
`;

const List = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  overflow: hidden;
`;

const Entry = styled.div`
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  padding: 13px 15px;
  background: rgba(17, 27, 51, 0.3);

  &:last-child {
    border-bottom: none;
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
    margin: 0px;
  }

  .name {
    font-size: 13px;
    font-weight: 700;
    color: var(--white);
  }

  .actions {
    display: inline-flex;
    align-items: center;
    gap: 9px;
  }
`;

const OpenLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(159, 176, 255, 0.95);
  text-decoration: none;
  flex-shrink: 0;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: rgb(159, 176, 255);
    border-color: rgba(255, 255, 255, 0.22);
  }
`;

const DetailsButton = styled.button`
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(213, 221, 238, 0.92);
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const Details = styled.div`
  margin-top: 9px;
  padding: 10px;
  background: rgba(8, 14, 30, 0.5);
  border-radius: 8px;
  font: 500 12px / 1.7 'JetBrains Mono', monospace;
  color: rgba(205, 214, 234, 0.78);
  word-break: break-all;

  a {
    color: rgb(159, 176, 255);
    text-decoration: underline;
  }
`;

const Empty = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
`;

const NoActive = styled.div`
  padding: 18px 15px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.3);
  text-align: center;
  font-size: 13px;
  color: rgba(176, 186, 209, 0.55);
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0px;
  font: 600 11px / 1 'Inter', sans-serif;
  letter-spacing: 0.04em;
  color: rgba(176, 186, 209, 0.55);

  &::before,
  &::after {
    content: '';
    flex: 1 1 0%;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const WebPlayer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 22px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
`;

const FieldRow = styled.div`
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .label {
    font-weight: 600;
    font-size: 12px;
    color: rgba(176, 186, 209, 0.65);
  }

  .editor {
    display: flex;
    gap: 8px;
    margin-top: 11px;
  }
`;


const RedirectInput = styled.input`
  flex: 1 1 0%;
  background: rgba(8, 14, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgb(240, 242, 245);
  padding: 9px 11px;
  font: 500 13px / 1 'Inter', sans-serif;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const SaveButton = styled.button`
  border-radius: 8px;
  padding: 9px 16px;
  white-space: nowrap;
  box-shadow: rgba(78, 95, 253, 0.32) 0px 6px 16px;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  transition: opacity 0.15s;

  &:hover,
  &:focus {
    outline: none;
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

type CmdVariant = 'danger' | 'default' | 'primary';

const Commands = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .label {
    font-weight: 600;
    font-size: 12px;
    color: rgba(176, 186, 209, 0.65);
  }

  .label.danger {
    display: flex;
    align-items: center;
    gap: 7px;
    color: rgb(255, 210, 122);
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 9px;
`;

const DangerNote = styled.p`
  font: 400 11.5px / 1.5 'Inter', sans-serif;
  color: rgba(176, 186, 209, 0.55);
  margin-top: 9px;
`;

const CmdButton = styled.button<{ $variant: CmdVariant }>`
  padding: 8px 13px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  ${({ $variant }): FlattenSimpleInterpolation => {
    if ($variant === 'primary') {
      // Fill + text color come from the shared .cc-primary class.
      return css`
        border: none;
        box-shadow: rgba(78, 95, 253, 0.32) 0px 6px 16px;

        &:hover,
        &:focus {
          outline: none;
          opacity: 0.92;
        }
      `;
    }

    if ($variant === 'danger') {
      return css`
        background: rgba(255, 80, 95, 0.12);
        border: 1px solid rgba(255, 80, 95, 0.4);
        color: rgb(255, 138, 147);

        &:hover,
        &:focus {
          outline: none;
          background: rgba(255, 80, 95, 0.2);
        }
      `;
    }

    return css`
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.12);
      color: rgba(213, 221, 238, 0.92);

      &:hover,
      &:focus {
        outline: none;
        background: rgba(255, 255, 255, 0.06);
      }
    `;
  }}
`;

const BannerEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 11px;

  .row {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    margin:0px;
  }

  /* Make the predefined-message Select fill the width, label left / caret right. */
  .dropdown {
    width: 100%;
  }

  button.dropdown-toggle {
    width: 100%;
    justify-content: space-between;
  }
`;

const BannerTextarea = styled.textarea`
  flex: 1 1 0%;
  width: 100%;
  background: rgba(8, 14, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(213, 221, 238, 0.92);
  padding: 10px;
  font-size: 14px;
  font-weight: 400;
  min-height: 72px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const BANNER_MESSAGES: SelectOption<string>[] = [
  {
    value: "We'll be starting shortly. Thanks for your patience.",
    label: "We'll be starting shortly. Thanks for your patience."
  },
  {
    value: 'Experiencing technical difficulties — please stand by.',
    label: 'Experiencing technical difficulties — please stand by.'
  },
  { value: 'Stream will resume in a few minutes.', label: 'Stream will resume in a few minutes.' },
  { value: 'Thank you for watching.', label: 'Thank you for watching.' }
];

interface Command {
  label: string;
  /** Tooltip help text (mirrors the legacy button's help copy). */
  tip: string;
  /** Confirm-dialog title. */
  title: string;
  /** Confirm-dialog body. */
  content: string;
  variant?: CmdVariant;
  /** Disabled by phase/availability, independent of the running state. */
  disabled?: boolean;
  /** Runs after the user confirms. */
  action: () => Promise<void>;
}

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const CommandButton = ({
  command,
  busy,
  disabled,
  onClick
}: {
  command: Command;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}): ReactElement => (
  <Tooltip placement='bottom-left' content={command.tip}>
    <CmdButton
      type='button'
      $variant={command.variant ?? 'default'}
      className={command.variant === 'primary' ? 'cc-primary' : undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {busy && <i className='fad fa-spinner-third fa-spin' style={{ marginRight: 6 }} />}
      {command.label}
    </CmdButton>
  </Tooltip>
);


const detailRows = (destination: EventDestination): DetailRow[] => {
  const url = destination.streamUrl ?? destination.destinationPlayerUrl;

  return [
    { label: 'stream name', value: destination.name ?? '' },
    { label: 'url', value: url ?? '', link: true },
    { label: 'stream key', value: destination.streamKey ?? '' },
    { label: 'passthrough', value: destination.streamPasstrough ?? '' }
  ].filter((row) => !!row.value);
};

/*
 * Takes what it draws rather than a destination, so Zoom — which is a pair of fields on the event,
 * not an EventDestination — can be one of these rows instead of a lookalike that drifts out of
 * step with it.
 */
const DestinationEntry = ({
  name,
  status,
  rows,
  link,
  needsReconnect
}: {
  name: string;
  status: string;
  rows: DetailRow[];
  link?: string | null;
  /* The client's saved destination lost its authorisation; nothing will reach it until relinked. */
  needsReconnect?: boolean;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);

  const streaming = status === 'Streaming';

  return (
    <Entry>
      <div className='row'>
        <span className='name'>{name}</span>

        {needsReconnect && (
          <Tag
            size='small'
            className='cc-amber'
            icon='far fa-exclamation-triangle'
            style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
          >
            Reconnect required
          </Tag>
        )}

        <span className='actions'>
          {!!link && (
            <OpenLink href={link} target='_blank' rel='noreferrer' title='Open destination'>
              <i className='far fa-external-link' />
            </OpenLink>
          )}

          {!!status && (
            <Tag
              size='small'
              className={
                status === NOT_DELIVERING ? 'cc-red' : streaming ? 'cc-green' : 'cc-neutral'
              }
              icon='fas fa-circle'
              iconSize={7}
              style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              {status}
            </Tag>
          )}

          {rows.length > 0 && (
            <DetailsButton
              type='button'
              onClick={(): void => {
                setExpanded(!expanded);
              }}
            >
              {expanded ? 'Hide' : 'Details'}
            </DetailsButton>
          )}
        </span>
      </div>

      {expanded && rows.length > 0 && (
        <Details>
          {rows.map((row) => (
            <div key={row.label}>
              {row.label}:{' '}
              {row.link ? (
                <a href={row.value} target='_blank' rel='noreferrer'>
                  {row.value}
                </a>
              ) : (
                row.value
              )}
            </div>
          ))}
        </Details>
      )}
    </Entry>
  );
};

const WebPlayerSection = ({ scheduledEvent }: Props): ReactElement => {
  const [updateEvent, { loading }] = useUpdateEventAdminFields();
  const [redirectEnabled, setRedirectEnabled] = useState(!!scheduledEvent.redirect_url);
  const [redirectValue, setRedirectValue] = useState(scheduledEvent.redirect_url ?? '');
  const [bannerEnabled, setBannerEnabled] = useState(!!scheduledEvent.banner);
  const [bannerValue, setBannerValue] = useState(scheduledEvent.banner ?? '');

  const saveRedirect = (): void => {
    void updateEvent({ id: Number(scheduledEvent.id), redirectURL: redirectValue });
  };

  const saveBanner = (): void => {
    void updateEvent({ id: Number(scheduledEvent.id), banner: bannerValue });
  };

  // Lifecycle / control-panel actions (same mutations the legacy webplayer used).
  const confirm = useConfirm();
  const alert = useAlert();
  const [running, setRunning] = useState<string>();
  const [forceLink] = useStream('forceStream');
  const [prepareSignal] = useStream('streamSignal');
  const [startEvent] = useStream('startEvent');
  const [stopEvent] = useStream('stopEvent');
  const [refresh] = useDefcon('defconCacheRefresh');
  const [refreshSignal] = useDefcon('defconCacheRefreshAndSignal');
  const [muxSync] = useDefcon('defconNuclearRefresh');
  const [browserRefresh] = useDefcon('defconForceBrowserRefresh');
  const [forceEnd] = useDefcon('defconForceEndStream');

  const eventId = Number(scheduledEvent.id);
  const locationId = scheduledEvent.location?.id ?? '';
  const cid = String(scheduledEvent.location?.user?.id ?? '');

  // Same gating the legacy Stream / Control Panel used.
  const isLive = scheduledEvent.status === Event.Phase.Live.toString();

  const isUpcoming = !!(
    scheduledEvent.start_time && DateTime.fromISO(scheduledEvent.start_time) > DateTime.now()
  );

  // The legacy Control Panel (defcon) only showed for upcoming/live events and needs a client id.
  const controlDisabled = (!isUpcoming && !isLive) || !cid;

  const lifecycleCommands: Command[] = [
    {
      label: 'Link Event to Stream',
      tip: 'Link current inbound stream to this event.',
      title: 'Link event to stream',
      content: 'This links the current inbound stream to this event. Continue?',
      variant: 'primary',
      action: async (): Promise<void> => {
        await forceLink({ eventToLink: eventId });
      }
    },
    {
      label: 'Move to Past Event',
      tip: 'Move Event from Live to Past Events.',
      title: 'Stop event',
      content: 'Are you sure you want to stop the event?',
      disabled: !isLive,
      action: async (): Promise<void> => {
        await stopEvent({ location: locationId, event: eventId });
        location.reload();
      }
    },
    {
      label: 'Force Start Event',
      tip: 'Move Event from Upcoming to Live.',
      title: 'Start event',
      content: 'Are you sure you want to start the event?',
      disabled: !isUpcoming,
      action: async (): Promise<void> => {
        await prepareSignal({ event: eventId });
        await sleep(30_000);
        await startEvent({ location: locationId, event: eventId });
        location.reload();
      }
    },
    {
      label: 'Refresh',
      tip: 'Syncs the Web Player with the Asset Service.',
      title: 'Are your sure you want to refresh the stream?',
      content: 'This action will sync the Web Player with the Asset Service.',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await refresh(cid);
      }
    },
    {
      label: 'Refresh + Send Signal',
      tip: 'Syncs Web Player with the Asset Service and sends signal to auto start.',
      title: 'Are your sure you want to refresh the stream and send auto start signal?',
      content:
        'This action will sync the Web Player with the Asset Service and send the Start Signal to the Web Player so that the video auto starts.',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await refreshSignal(cid);
      }
    },
    {
      label: 'Refresh + Mux Sync',
      tip: 'Syncs Asset Service with Mux, then triggers "Refresh + Start Signal".',
      title: 'Are your sure you want to refresh the stream and trigger "Refresh + Start Signal"?',
      content:
        'This action will take a few seconds to complete as it needs to re-sync Mux’s database with ours, and then trigger the “Refresh + Start Signal” action.',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await muxSync(cid);
      }
    }
  ];

  const dangerCommands: Command[] = [
    {
      label: 'Browser Refresh',
      tip: "Forces everyone's browser to refresh at the same time.",
      title: "Are your sure you want to force everyone's browser to refresh at the same time?",
      content:
        'This action will cause the browser of anyone watching our web player to refresh, as if someone had pressed the “Reload button” on their browser.',
      variant: 'danger',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await browserRefresh(cid);
      }
    },
    {
      label: 'Force End',
      tip: 'Ends the stream for everyone.',
      title: 'Are your sure you want to end the stream for everyone?',
      content: 'This action will stop and end the live stream for everyone.',
      variant: 'danger',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await forceEnd(cid);
      }
    }
  ];

  const runCommand = (command: Command): void => {
    void (async (): Promise<void> => {
      const confirmed = await confirm({
        title: command.title,
        message: command.content,
        tone: command.variant === 'danger' ? 'danger' : 'default'
      });

      if (!confirmed) {
        return;
      }

      setRunning(command.label);
      alert.clear();

      try {
        await command.action();
      } catch (error: unknown) {
        // The stream mutations reject with the backend's message; show it instead of failing silently.
        alert.show(error instanceof Error ? error.message : `${command.label} failed.`);
      }

      setRunning(undefined);
    })();
  };

  return (
    <WebPlayer>
      <SectionTitle>Web Player</SectionTitle>

      <FieldRow>
        <div className='head'>
          <span className='label'>Redirect URL</span>
          <Toggle
            on={redirectEnabled}
            onChange={(): void => {
              setRedirectEnabled(!redirectEnabled);
            }}
          />
        </div>

        {redirectEnabled && (
          <div className='editor'>
            <RedirectInput
              type='text'
              placeholder='https://…'
              value={redirectValue}
              onChange={(event): void => {
                setRedirectValue(event.target.value);
              }}
            />
            <SaveButton className='cc-primary' type='button' disabled={loading} onClick={saveRedirect}>
              Save
            </SaveButton>
          </div>
        )}
      </FieldRow>

      <FieldRow>
        <div className='head'>
          <span className='label'>Player Banner</span>
          <Toggle
            on={bannerEnabled}
            onChange={(): void => {
              setBannerEnabled(!bannerEnabled);
            }}
          />
        </div>

        {bannerEnabled && (
          <BannerEditor>
            <Select
              value={bannerValue}
              options={BANNER_MESSAGES}
              placeholder='Insert a predefined message…'
              onChange={setBannerValue}
            />
            <div className='row'>
              <BannerTextarea
                placeholder='Write a message shown to every viewer…'
                value={bannerValue}
                onChange={(event): void => {
                  setBannerValue(event.target.value);
                }}
              />
              <SaveButton className='cc-primary' type='button' disabled={loading} onClick={saveBanner}>
                Push
              </SaveButton>
            </div>
          </BannerEditor>
        )}
      </FieldRow>

      <Commands>
        {alert.message !== undefined && <Alert message={alert.message} onDismiss={alert.clear} />}

        <div>
          <div className='label'>Lifecycle commands</div>
          <Buttons>
            {lifecycleCommands.map((command) => (
              <CommandButton
                key={command.label}
                command={command}
                busy={running === command.label}
                disabled={running !== undefined || command.disabled === true}
                onClick={(): void => {
                  runCommand(command);
                }}
              />
            ))}
          </Buttons>
        </div>

        <div>
          <div className='label danger'>
            <i className='fas fa-exclamation-triangle' /> Danger Zone
          </div>
          <Buttons>
            {dangerCommands.map((command) => (
              <CommandButton
                key={command.label}
                command={command}
                busy={running === command.label}
                disabled={running !== undefined || command.disabled === true}
                onClick={(): void => {
                  runCommand(command);
                }}
              />
            ))}
          </Buttons>
          <DangerNote>
            Force End terminates the live stream for all viewers across every destination and cannot
            be resumed.
          </DangerNote>
        </div>
      </Commands>
    </WebPlayer>
  );
};

export const DistributionTab = ({ scheduledEvent }: Props): ReactElement => {
  const isLive = scheduledEvent.status === Event.Phase.Live.toString();

  const destinations = scheduledEvent.destinations ?? [];

  /*
   * The event's destinations come from Simulcasting and carry no authorisation state; whether a
   * destination is still linked lives on the client's saved integrations, which is a separate
   * query. Fetched here and matched by name — the same join the customer dashboard makes when it
   * decides which destinations can be picked for an event.
   *
   * `validate: true` re-checks the authorisation instead of trusting a stored flag, matching the
   * destinations grid in the customer dashboard. It costs a round trip to the provider, and this
   * is exactly the screen where the answer has to be current: an ES doing pre-checks wants the
   * truth now, not whatever was true last time somebody looked.
   */
  const { destinations: clientDestinations = [] } = Scheduler.Destination.useDestinations(
    String(scheduledEvent.location?.user?.id ?? ''),
    true
  );

  const key = (name?: string | null): string => (name ?? '').trim().toLowerCase();

  /*
   * Simulcasting answers the per-event call with destinations the event does not use — a client's
   * spare YouTube channel comes back alongside the two it actually pushes to. What the event uses
   * is its own integration rows, and those carry the integration id, so the join runs id to id.
   * That is the only exact key available: EventDestination has no id of its own.
   */
  const eventIntegrationIds = new Set(
    (scheduledEvent.integrations ?? []).map((integration) => integration.integration)
  );

  const eventIntegrations = clientDestinations.filter((destination) =>
    eventIntegrationIds.has(destination.id)
  );

  const usedNames = new Set(eventIntegrations.map((destination) => key(destination.name)));

  /* Both sides answered and met: until then the stream flags stand in, below. */
  const resolvedById = eventIntegrationIds.size > 0 && eventIntegrations.length > 0;

  /*
   * The fallback, and the reason it is sound: these flags are the event's integration rows folded
   * up by platform, so they agree with the id join by construction — they just cannot tell two
   * Facebook pages apart. Anything of an unrecognised type is shown rather than hidden.
   */
  const matchesStreamFlag = (destination: EventDestination): boolean => {
    switch (key(destination.type)) {
      case 'rtmp':
        return Boolean(scheduledEvent.stream_rtmp);
      case 'facebook':
        return Boolean(scheduledEvent.stream_facebook);
      case 'youtube':
        return Boolean(scheduledEvent.stream_youtube);
      default:
        return true;
    }
  };

  /* Not a Simulcasting destination: the resolver appends it to every event, typeless. */
  const isLiveControlPlayer = (destination: EventDestination): boolean =>
    destination.type === 'LiveControl' || destination.name === LIVECONTROL_PLAYER;

  const belongsToEvent = (destination: EventDestination): boolean => {
    if (isLiveControlPlayer(destination)) {
      return Boolean(scheduledEvent.stream_live);
    }

    return resolvedById ? usedNames.has(key(destination.name)) : matchesStreamFlag(destination);
  };

  const deauthorizedNames = new Set(
    clientDestinations
      .filter((destination) => destination.deauthorized)
      .map((destination) => key(destination.name))
  );

  /*
   * Read off the matched integration when the id join held, so the answer is about this exact
   * destination. The name lookup is the fallback: two integrations sharing a name would both light
   * up, which is the harmless side to err on — it prompts a check rather than hiding a dead output.
   */
  const needsReconnect = (destination: EventDestination): boolean => {
    const matched = eventIntegrations.find(
      (integration) => key(integration.name) === key(destination.name)
    );

    return matched ? matched.deauthorized : deauthorizedNames.has(key(destination.name));
  };

  /*
   * A destination's `active` flag can arrive null even while streaming, so we key
   * off the effective status instead (LiveControl reports live from the event).
   *
   * A live event pushing to an unlinked destination reads as Not delivering: the stream is going
   * out and landing nowhere, which is the case this screen exists to catch before it matters.
   */
  const isStreaming = (destination: EventDestination): boolean =>
    destination.type === 'LiveControl' && isLive ? true : destination.status === 'Streaming';

  const effectiveStatus = (destination: EventDestination): string => {
    if (!isStreaming(destination)) {
      return destination.status ?? '';
    }

    return needsReconnect(destination) ? NOT_DELIVERING : 'Streaming';
  };

  /*
   * Grouped by whether the event is pushing to them, not by the label. A destination that is
   * streaming into a dead link belongs beside the healthy ones — that juxtaposition is the point.
   * Filing it under Inactive would bury exactly what an ES is scanning for.
   */
  const eventDestinations = destinations.filter(belongsToEvent);

  const active = eventDestinations.filter(isStreaming);

  const inactive = eventDestinations.filter((destination) => !isStreaming(destination));

  /*
   * Zoom is a pair of fields on the event, not a destination, so it is built here rather than
   * filtered out of `destinations` — putting it through those filters would mean faking one.
   *
   * Its status is derived exactly the way LiveControl's is a few lines up: Zoom reports nothing
   * back, and neither does LiveControl, so "the event is live and this output is configured" is
   * the same claim in both cases.
   */
  const showZoom = Boolean(scheduledEvent.stream_zoom);
  const zoomStreaming = showZoom && isLive;

  /*
   * The fallback row is the point of the whole change: detailRows drops empty values, so without
   * it a Zoom event with nothing saved would render a row with no Details button — which is the
   * silence that prompted this in the first place.
   */
  const zoomRows: DetailRow[] =
    scheduledEvent.zoom_meeting_id || scheduledEvent.zoom_password
      ? [
          { label: 'meeting id', value: scheduledEvent.zoom_meeting_id ?? '' },
          { label: 'password', value: scheduledEvent.zoom_password ?? '' }
        ].filter((row) => !!row.value)
      : [{ label: 'meeting id', value: 'not configured' }];

  const zoomEntry = <DestinationEntry name='Zoom' status={zoomStreaming ? 'Streaming' : 'IDLE'} rows={zoomRows} />;

  return (
    <div>
      <SectionTitle>Destinations</SectionTitle>

      {/* Zoom counts towards "has destinations", or an event with only Zoom reads as having none. */}
      {eventDestinations.length === 0 && !showZoom ? (
        <Empty>No destinations for this event.</Empty>
      ) : (
        <>
          {active.length > 0 || zoomStreaming ? (
            <List>
              {zoomStreaming && zoomEntry}
              {active.map((destination, index) => (
                <DestinationEntry
                  key={destination.streamBroadcastId ?? destination.name ?? index}
                  name={destination.name ?? destination.type ?? ''}
                  status={effectiveStatus(destination)}
                  rows={detailRows(destination)}
                  link={destination.destinationPlayerUrl}
                  needsReconnect={needsReconnect(destination)}
                />
              ))}
            </List>
          ) : (
            <NoActive>No active destinations</NoActive>
          )}

          {(inactive.length > 0 || (showZoom && !zoomStreaming)) && (
            <>
              <Divider>Inactive</Divider>
              <List>
                {showZoom && !zoomStreaming && zoomEntry}
                {inactive.map((destination, index) => (
                  <DestinationEntry
                    key={destination.streamBroadcastId ?? destination.name ?? index}
                    name={destination.name ?? destination.type ?? ''}
                    status='IDLE'
                    rows={detailRows(destination)}
                    link={destination.destinationPlayerUrl}
                    needsReconnect={needsReconnect(destination)}
                  />
                ))}
              </List>
            </>
          )}
        </>
      )}

      <WebPlayerSection scheduledEvent={scheduledEvent} />
    </div>
  );
};
